import { renderDonutForPdf, renderBarForPdf, renderDualAxesForPdf, drawChartRow, drawChartSection } from "../pdfCharts";
import { BAR_PALETTE, DONUT_COLORS } from "../../../../utils/chartConfig";
import { formatWhole, formatMagnitudeAdaptive } from "../../../../utils/numberFormat";
import { getWaterSourceTotals, buildWaterBarChartData, buildWaterUsageData, waterGroupLabels, WATER_SOURCE_LABEL_KEYS, WATER_USAGE_LABEL_KEYS } from "./TableGraf";
import { buildWaterComparisonData, buildWaterProductionIntensityData, buildWaterDomesticIntensityData, buildSharedYScale } from "./waterChartData";

// "Enero" -> "Ene", etc. — con los gráficos compartiendo página el nombre
// completo del mes no entra sin superponerse (mismo ajuste que en Energía/Combustibles).
const monthLabel3 = (v) => String(v).slice(0, 3);

// Arma los 3 gráficos de TableGraf.js (dona Fuentes + barra Consumo + dona
// Usos) como SVG vectorial para el PDF — mismo orden y lógica de datos que
// ya usa TableGraf.js en pantalla, reutilizada tal cual.
export async function renderWaterChartsForPdf(totales, filters, t) {
  const dataFuente = getWaterSourceTotals(totales, filters?.year);
  const donutSourcesData = Object.entries(dataFuente)
    .filter(([, { total }]) => total > 0)
    .map(([groupKey, { total }]) => {
      const type = waterGroupLabels[groupKey];
      return { type, value: total, label: t(WATER_SOURCE_LABEL_KEYS[type] || type) };
    });

  const donutUsageData = buildWaterUsageData(totales, filters?.year).map((d) => ({
    ...d,
    label: t(WATER_USAGE_LABEL_KEYS[d.type] || d.type),
  }));

  const [donutSources, barConsumo, donutUsage] = await Promise.all([
    renderDonutForPdf(donutSourcesData, DONUT_COLORS.water),
    // Regla 1 (AJUSTES DE MAGNITUDES NUMÉRICAS): espacio fino separador de
    // miles, sin decimales — mismo criterio que ya tienen las secciones
    // DualAxes de más abajo.
    renderBarForPdf(buildWaterBarChartData(totales), "M3", BAR_PALETTE, { yFormatter: formatWhole }),
    renderDonutForPdf(donutUsageData, DONUT_COLORS.waterUsage),
  ]);

  return { donutSources, barConsumo, donutUsage };
}

// "Comparación consumo total" — barras de consumo mensual por año + línea
// punteada de promedio, misma config que WaterComparisonChart.
export async function renderWaterComparisonForPdf(resultsByYear, t) {
  const { years, barData, lineData, yearColorMap } = buildWaterComparisonData(resultsByYear, t);
  const sharedYScale = buildSharedYScale(barData, lineData);

  const children = [
    {
      data: barData,
      type: "interval",
      yField: "value",
      colorField: "year",
      group: true,
      style: { maxWidth: 40 },
      scale: { color: ({ year }) => yearColorMap[year], y: sharedYScale },
    },
    {
      data: lineData,
      type: "line",
      yField: "value",
      colorField: "year",
      axis: { y: false },
      scale: { y: sharedYScale },
      style: { lineWidth: 2, lineDash: [4, 8] },
    },
  ];

  const result = await renderDualAxesForPdf(children, {
    xField: "month",
    scale: {
      color: { domain: Object.keys(yearColorMap), range: Object.values(yearColorMap) },
      x: { paddingOuter: 0.1 },
    },
    axis: {
      x: { title: false, labelAutoHide: false, labelAutoRotate: false, labelFill: "#000", labelFontSize: 8, labelFormatter: monthLabel3 },
      y: { title: false, grid: true, labelFill: "#000", labelFontSize: 8, labelFormatter: (v) => formatWhole(v) },
    },
  });
  if (result.isEmpty) return result;

  const legendItems = years.map((year) => ({ label: String(year), color: yearColorMap[year] }));
  return { ...result, legendItems };
}

// "Intensidad industrial" — consumo/producción mensual (línea sólida) +
// promedio (línea punteada), misma config que WaterProductionIntensityChart.
export async function renderWaterProductionIntensityForPdf(resultsByYear, resultsProductions, t) {
  const { years, consumptionData, averageData, yearColorMap } = buildWaterProductionIntensityData(resultsByYear, resultsProductions, t);
  const sharedYScale = buildSharedYScale(consumptionData, averageData);

  const children = [
    {
      data: consumptionData,
      type: "line",
      yField: "value",
      colorField: "year",
      style: { lineWidth: 4 },
      scale: { y: sharedYScale },
      connectNulls: false,
    },
    {
      data: averageData,
      type: "line",
      yField: "value",
      colorField: "year",
      axis: { y: false },
      scale: { y: sharedYScale },
      style: { lineWidth: 2, lineDash: [4, 8] },
      connectNulls: false,
    },
  ];

  const result = await renderDualAxesForPdf(children, {
    xField: "month",
    scale: { color: { domain: Object.keys(yearColorMap), range: Object.values(yearColorMap) } },
    axis: {
      x: { title: false, labelFill: "#000", labelFontSize: 8, labelFormatter: monthLabel3 },
      y: { title: false, labelFill: "#000", labelFontSize: 8, labelFormatter: (v) => formatMagnitudeAdaptive(v) },
    },
  });
  if (result.isEmpty) return result;

  const legendItems = years.map((year) => ({ label: String(year), color: yearColorMap[year] }));
  return { ...result, legendItems };
}

// "Intensidad doméstica" — mismo patrón que "Intensidad industrial", con
// datos por trabajador en vez de por unidad producida.
export async function renderWaterDomesticIntensityForPdf(resultsByYear, resultsWorkers, t) {
  const { years, consumptionData, averageData, yearColorMap } = buildWaterDomesticIntensityData(resultsByYear, resultsWorkers, t);
  const sharedYScale = buildSharedYScale(consumptionData, averageData);

  const children = [
    {
      data: consumptionData,
      type: "line",
      yField: "value",
      colorField: "year",
      group: true,
      style: { maxWidth: 80, lineWidth: 4 },
      scale: { y: sharedYScale },
      connectNulls: false,
    },
    {
      data: averageData,
      type: "line",
      yField: "value",
      colorField: "year",
      axis: { y: false },
      scale: { y: sharedYScale },
      style: { lineWidth: 2, lineDash: [4, 8] },
      connectNulls: false,
    },
  ];

  const result = await renderDualAxesForPdf(children, {
    xField: "month",
    scale: { color: { domain: Object.keys(yearColorMap), range: Object.values(yearColorMap) } },
    axis: {
      x: { title: false, labelFill: "#000", labelFontSize: 8, labelFormatter: monthLabel3 },
      y: { title: false, labelFill: "#000", labelFontSize: 8, labelFormatter: (v) => formatWhole(v) },
    },
  });
  if (result.isEmpty) return result;

  const legendItems = years.map((year) => ({ label: String(year), color: yearColorMap[year] }));
  return { ...result, legendItems };
}

// Orquesta el dibujado de los 6 gráficos de Agua en el PDF: se pasa como
// `renderCustomCharts` a KPIExportIconButton. Mismo criterio de layout ya
// resuelto en Energía/Combustibles: fila de 3 tarjetas (2 donas + 1 barra)
// con altura fija, y las 3 secciones DualAxes repartiéndose lo que quede de
// la página (o una hoja nueva si no entran).
export async function drawWaterCustomCharts(doc, ctx, { totales, filters, resultsByYear, resultsProductions, resultsWorkers, t }) {
  const { margin, pageWidth, pageHeight, imgWidth } = ctx;
  let yOffset = ctx.yOffset;

  const { donutSources, barConsumo, donutUsage } = await renderWaterChartsForPdf(totales, filters, t);
  const waterCards = [
    { title: t("water_sources"), ...donutSources },
    { title: t("water_consumption"), ...barConsumo },
    { title: t("usage_type"), ...donutUsage },
  ];
  const CHART_ROW_H = 210;
  const remaining = pageHeight - margin - yOffset;
  if (CHART_ROW_H > remaining) {
    doc.addPage();
    yOffset = margin;
  }
  await drawChartRow(doc, waterCards, { x: margin, y: yOffset, availW: imgWidth, cardH: CHART_ROW_H, emptyMessage: t("no_data_available") });
  yOffset += CHART_ROW_H + 10;
  donutSources.cleanup?.();
  barConsumo.cleanup?.();
  donutUsage.cleanup?.();

  // 3 secciones DualAxes, de a 2 por hoja (no las 3 juntas — quedaban muy
  // bajitas). La que sobra (la 3ra) arranca hoja nueva; con un techo de
  // altura (no usa TODO lo que quede de la hoja) para que, si sobra lugar,
  // la tabla pueda arrancar debajo en la misma hoja en vez de una en blanco.
  const GAP = 10;
  const PER_PAGE = 2;
  const MIN_EACH_H = 240;
  const MAX_EACH_H = 320;
  const sections = [
    { title: t("water_total_consumption_comparison_3_years"), render: () => renderWaterComparisonForPdf(resultsByYear, t) },
    { title: t("industrial_water_intensity"), render: () => renderWaterProductionIntensityForPdf(resultsByYear, resultsProductions, t) },
    { title: t("domestic_water_intensity"), render: () => renderWaterDomesticIntensityForPdf(resultsByYear, resultsWorkers, t) },
  ];

  for (let i = 0; i < sections.length; i += PER_PAGE) {
    const group = sections.slice(i, i + PER_PAGE);
    let availForGroup = pageHeight - margin - yOffset - GAP * (group.length - 1);
    if (availForGroup < MIN_EACH_H * group.length) {
      doc.addPage();
      yOffset = margin;
      availForGroup = pageHeight - margin - yOffset - GAP * (group.length - 1);
    }
    const sectionH = Math.min(Math.floor(availForGroup / group.length), MAX_EACH_H);

    for (const section of group) {
      const result = await section.render();
      await drawChartSection(doc, { x: margin, y: yOffset, width: imgWidth, height: sectionH, title: section.title, emptyMessage: t("no_data_available"), ...result });
      yOffset += sectionH + GAP;
      result.cleanup?.();
    }
  }

  return yOffset;
}
